import { useContext, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";

import { formatResolvedPriceString } from "@/components/care/utils";
import {
  trackCardEvent,
  trackCardViewed,
  type CardSource,
} from "@/components/chat/chatCardEvents";
import {
  addStatus,
  cardAdd,
  offerImageUrl,
  startAdd,
} from "@/components/chat/ChatCommerceCard";
import { CARD_CLASSES } from "@/components/chat/ChatProductCard";
import ChatProgramAddedBar from "@/components/chat/ChatProgramAddedBar";
import ChatProgramItems from "@/components/chat/ChatProgramItems";
import ChatViewCart from "@/components/chat/ChatViewCart";
import { useCartPanelMounted } from "@/components/ecomm/cartPanelPresence";
import { ChevronDown, CTA, Warning } from "@/components/ui";
import { CartContext } from "@/contexts/cartContext";
import { cents, type ProgramOffer } from "@/services/commerceProgramOffers";
import {
  quoteAndAddProgram,
  type ProgramQuoteAndAddResult,
} from "@/services/commerceProgramOutcome";

// Figma: https://www.figma.com/design/fPDiaxbsQDLapw3qiul2us/-PROD--EvvyAI?node-id=2849-9801 (added: 3151-8011)
// px, not rem classes: the app root is 14px. Fixed text sizes: the chat is narrow at every width.
const CARD = `${CARD_CLASSES} flex w-full flex-col gap-[var(--chat-space-04)] bg-ds-background-base`;
const TITLE = "m-0 text-ds-body-base-medium text-ds-text-primary";
const FOCUS = "focus-visible:outline focus-visible:outline-2";
const MAX_THUMBNAILS = 5;
// Figma's whole dollars ("$183", "$203"); if either price has cents, both show them.
const programPrices = ({ price, full_price }: ProgramOffer) => {
  const whole = [price, full_price].every(
    (amount) => cents(amount) % 100 === 0
  );
  return [price, full_price].map((amount) =>
    whole
      ? formatResolvedPriceString(amount).replace(/\.00$/, "")
      : formatResolvedPriceString(amount)
  );
};
// Shared with restored programs (ChatSavedCommerceCard).
// Plain chat text, outside any card. Shared with restored programs (ChatSavedCommerceCard).
export const PLAN_UNAVAILABLE = (
  <div role="status" className="text-ds-body-base-regular text-ds-text-primary">
    This plan isn't available to add right now.
  </div>
);
export const programPdpPath = ({ pdp_slug }: Pick<ProgramOffer, "pdp_slug">) =>
  `/care/shop/bundle/${pdp_slug}/`;
// Shop Care's bundle button reads "Added" (Phase 2 product decisions, "Wording").
const ADDED_MESSAGE = "Added";
// Failure lines never name the reason: retryable, or part of the plan may be in the cart.
const ADD_FAILED_MESSAGE =
  "We couldn't add your plan. Please try again or check your cart.";
const MAYBE_ADDED_MESSAGE =
  "Your plan may not have been fully added. Please check your cart.";
const listNames = (names: string[]) =>
  new Intl.ListFormat("en", { type: "conjunction" }).format(names);
// Products kept as the cart's subscription lines are named from the decoded offer only.
const inCartAs = (names: string[]) =>
  `${listNames(names)} ${names.length === 1 ? "is" : "are"} already in your cart as ${names.length === 1 ? "a subscription" : "subscriptions"}`;
// Under the price: why the "N products" badge counts more than the price covers.
const priceNote = (lead: string, names: string[]) =>
  `${lead}, so ${names.length === 1 ? "it’s" : "they’re"} not included in this price.`;

/** A decoded treatment program: the plan, and a one-time add of its quoted set. */
export default function ChatProgramCard({
  offer: shown,
  sessionId,
  messageIndex,
  source,
}: {
  offer: ProgramOffer;
  sessionId: string;
  messageIndex: number;
  source: CardSource;
}) {
  const { setShowCart, refetchCart } = useContext(CartContext);
  const hasCartPanel = useCartPanelMounted();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const cardKey = `${sessionId}:${messageIndex}:${shown.offer_id}`;
  // Seeded once, as ChatCommerceCard: a reopened card waits on the same add.
  const [add, setAdd] = useState(() =>
    cardAdd<ProgramOffer, ProgramQuoteAndAddResult>(cardKey)
  );
  const [result, setResult] = useState(add?.settled);

  useEffect(() => {
    add?.result.then(setResult);
  }, [add]);

  // A changed quote shows the fresh program; the next tap adds exactly what is shown.
  const offer =
    result?.outcome === "review" ? result.offer : (add?.offer ?? shown);
  const { kind, product_slug, products } = offer;

  useEffect(
    () => trackCardViewed(cardKey, { kind, product_slug }, source),
    [cardKey, kind, product_slug, source]
  );

  const tap = (): void => {
    const next = startAdd(cardKey, offer, quoteAndAddProgram, (settled) => {
      // Subscribed products are shown as included but never added; kept ones were in the cart.
      const kept = "kept" in settled ? (settled.kept?.length ?? 0) : 0;
      trackCardEvent("added", offer, source, {
        itemsAdded: offer.action.items.length - kept,
        itemsShown: offer.products.length,
      });
      refetchCart();
      // The minicart takes the foreground and the chat yields to it (ChatSidebar).
      if (hasCartPanel) setShowCart(true);
    });
    if (!next) return;
    // The Add tap, as on Phase 1's card, so viewed → clicked → added compares across kinds.
    trackCardEvent("addClicked", offer, source);
    setResult(undefined);
    setAdd(next);
  };

  const pending = !!add && !result;
  const outcome = result?.outcome;
  const status = addStatus(result);
  const partial = !!result && "partial" in result;
  // The quote prices only the lines the batch adds (S12); subscribed products are never added.
  const namesWhere = (flag: "subscribed" | "in_cart_subscription") =>
    products.filter((product) => product[flag]).map((product) => product.name);
  const subscribedNames = namesWhere("subscribed");
  const inCartNames = namesWhere("in_cart_subscription");
  const kept = result && "kept" in result ? result.kept : undefined;
  const added = outcome === "added";
  // ADDED! says a plain add; the price note names in-cart subscriptions, this line any others.
  const keptUnnoted = kept?.filter((name) => !inCartNames.includes(name)) ?? [];
  // Not added, or not confirmed: the member retries or checks the cart.
  const warn = status.isRetry || outcome === "not_added";
  // A retry would only find part of the plan in the cart; the cart is the way forward.
  const canTap = status.canTap && !partial;
  // Partly added or not confirmed (no retry) reads differently from a failure worth retrying.
  const message = warn
    ? canTap
      ? ADD_FAILED_MESSAGE
      : MAYBE_ADDED_MESSAGE
    : keptUnnoted.length > 0
      ? `${ADDED_MESSAGE} — ${inCartAs(keptUnnoted)}.`
      : added
        ? undefined
        : status.message;
  // The failure line says to check the cart, so every failure offers it (check-failed too).
  const canViewCart = status.canViewCart || warn;
  const gapAbove = message ? "mt-[var(--chat-space-03)]" : "";
  // Announces a plain add once; the ADDED! bar below is the way to the cart.
  const statusLine = (
    <div role="status">
      {message && (
        <p className="m-0 flex items-start gap-[var(--chat-space-02)] text-ds-body-xsmall-regular">
          {warn && (
            <Warning className="mt-px shrink-0 text-ds-ui-elements-error" />
          )}
          {message}
        </p>
      )}
      {added && !message && <span className="sr-only">Added</span>}
    </div>
  );
  // U8: once it is no longer offered, one line of chat text replaces the whole card.
  if (outcome === "unavailable") return PLAN_UNAVAILABLE;

  // The decoder guarantees full_price >= price.
  const fullCents = cents(offer.full_price);
  const savings = fullCents - cents(offer.price);
  // Shop Care claims a fixed "Save 10% w/ program": the badge shows only when that holds.
  const savePercent = Math.round((savings / fullCents) * 100);
  const [priceText, fullPriceText] = programPrices(offer);
  const priceNotes = [
    subscribedNames.length > 0 &&
      priceNote(
        `You already subscribe to ${listNames(subscribedNames)}`,
        subscribedNames
      ),
    inCartNames.length > 0 && priceNote(inCartAs(inCartNames), inCartNames),
  ].filter((note): note is string => !!note);
  const images = products
    .filter((product) => offerImageUrl(product))
    .slice(0, MAX_THUMBNAILS);

  return (
    <div className={CARD}>
      <div className="flex items-start justify-between gap-[var(--chat-space-03)]">
        <div className="flex min-w-0 flex-col gap-[var(--chat-space-03)]">
          <div className="flex flex-col items-start gap-[var(--chat-space-02)]">
            <p className={TITLE}>{offer.name}</p>
            <span className="inline-flex h-[20px] items-center rounded-[8px] bg-ds-background-dark-creme px-[var(--chat-space-03)] text-ds-body-xsmall-medium text-ds-text-primary">
              {products.length} {products.length === 1 ? "product" : "products"}
            </span>
          </div>
          {images.length > 0 && (
            <div className="flex">
              {images.map((product) => (
                <img
                  key={product.treatment_product_id}
                  src={offerImageUrl(product)}
                  alt={product.name}
                  className="-mr-[16px] size-[48px] shrink-0 rounded-[40px] border-2 border-solid border-ds-background-base object-cover last:mr-0"
                />
              ))}
            </div>
          )}
        </div>
        {/* A router link, so the chat sidebar stays open on the PDP. The padding widens the tap target. */}
        <Link
          to={programPdpPath(offer)}
          onClick={() => trackCardEvent("detailsClicked", offer, source)}
          aria-label={`View details for ${offer.name}`}
          className={`-m-[4px] shrink-0 p-[4px] text-ds-text-primary ${FOCUS}`}
        >
          <ChevronDown className="block -rotate-90" />
        </Link>
      </div>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={() => setExpanded(!expanded)}
        // unset opts out of app.css's uppercase button rule.
        className={`unset self-start text-ds-body-xsmall-regular normal-case tracking-normal text-ds-text-primary underline ${FOCUS}`}
      >
        {expanded ? "Hide details" : "See what’s included"}{" "}
        <span aria-hidden>{expanded ? "↑" : "↓"}</span>
      </button>
      {expanded && <ChatProgramItems id={listId} products={products} />}
      <div
        aria-hidden
        className="border-0 border-t border-dashed border-ds-line-light-on-white"
      />
      <div className="flex flex-col gap-[var(--chat-space-02)]">
        <p className="m-0 flex flex-wrap items-center gap-[var(--chat-space-03)] text-ds-header-subheader uppercase">
          <span className="flex items-center gap-[var(--chat-space-02)]">
            <span className="text-ds-text-primary">{priceText}</span>
            {savings > 0 && (
              <>
                <span className="sr-only">Regular price</span>
                <s className="text-ds-text-light-on-white">{fullPriceText}</s>
              </>
            )}
          </span>
          {savePercent === 10 && (
            // Not Figma's #41907A: white 12px text on it is 3.82:1, under WCAG AA's 4.5:1.
            <span className="rounded-ds-corner-full bg-ds-all-colors-pond px-[12px] py-[4px] text-ds-text-white">
              Save 10%
            </span>
          )}
        </p>
        {priceNotes.map((note) => (
          <p
            key={note}
            className="m-0 text-ds-body-xsmall-regular text-ds-text-light-on-white"
          >
            {note}
          </p>
        ))}
      </div>
      <div className="flex flex-col">
        {statusLine}
        {added ? (
          <ChatProgramAddedBar className={gapAbove} />
        ) : (
          (canTap || canViewCart) && (
            <div
              className={`grid auto-cols-fr grid-flow-col gap-[var(--chat-space-03)] ${gapAbove}`}
            >
              {canTap && (
                <CTA
                  variant="tertiary-black"
                  size="mini"
                  onClick={tap}
                  disabled={pending}
                  className={`w-full rounded-ds-corner-small ${FOCUS}`}
                >
                  {pending
                    ? "Adding…"
                    : status.isRetry
                      ? "Try again"
                      : "Add program to cart"}
                </CTA>
              )}
              {canViewCart && <ChatViewCart />}
            </div>
          )
        )}
      </div>
    </div>
  );
}
